// Object.hasOwn polyfill：必须保持为第一个 import，覆盖旧 WebView
import "@/utils/polyfills/objectHasOwn";
import "./styles/global.scss";
// FCP/LCP 优化前提：必须在 entry 顶层 import "@/i18n" 显式触发 i18n.init()。
// 不阻塞 entry 评估，但确保事件循环里它已经在 loading 状态，避免警告刷屏。
import "@/i18n";
import { createRoot } from "react-dom/client";
import { getObject, setObject } from "@/utils/storage-helpers";
import { bootstrapDomainGuard } from "@/modules/domainGuard";
import { initPwa, initWebPush, loopCheckPwa } from "@/modules/pwa/init";

// 在任何 axios 请求触发前释放 apiGate；与下方预加载并行，不阻塞首屏
void bootstrapDomainGuard();

(() => {
  try {
    const sd = window.__STARTUP_DATA__;
    if (!sd || sd.status !== 0) return;

    if (sd.buildConfig) {
      if (!window._config) {
        (window as any)._config = sd.buildConfig;
      }
      const cfg = (window as any)._config;
      if (sd.buildConfig.varStyles && !cfg?.varStyles) {
        cfg.varStyles = sd.buildConfig.varStyles;
      }
      if (sd.buildConfig.varStyles) {
        document.body.style.cssText =
          (document.body.style.cssText || "") + sd.buildConfig.varStyles;
      }
    }

    const cfg = (window as any)._config;
    if (cfg) {
      if (sd.countries && !cfg.countryList) cfg.countryList = sd.countries;
      if (sd.countryId && !cfg.countryId) cfg.countryId = sd.countryId;
      if (sd.languages && !cfg.languageList) cfg.languageList = sd.languages;
      if (sd.language && !cfg.lang) cfg.lang = sd.language;
    }
  } catch {
    // ignore
  }
})();

// FCP/LCP 优化：Datadog RUM 延迟到 load 事件后初始化（与 main.tsx 保持一致）。
// 浏览器原生 Performance API 自动记录 FCP/LCP 等指标，RUM SDK 初始化后回溯
// 读取，延迟初始化不会丢失性能数据，但能把 RUM SDK（~150-200KB）的下载和
// 评估移出 LCP 关键路径，同时让 PerformanceObserverCallback /
// IdleRequestCallback 之类的内部 LongTask 推迟到 LCP 之后再出现。
// 采样档位 / SDK 配置统一收敛到 ./config/initRum；通过动态 import 保证
// 本 entry 静态闭包零 RUM 代码（含白名单数据 + SDK）。
window.addEventListener(
  "load",
  () => {
    import("./config/initRum")
      .then(({ initDatadogRum }) => initDatadogRum())
      .catch((e: unknown) => console.error("RUM init failed:", e));
  },
  { once: true }
);

// FCP/LCP 优化：antd-mobile 全局样式延后到 __idleBoot 里加载。
// 落地页首屏渲染（Header/AppCard/Info）不依赖 antd-mobile 全局样式即可
// 正常呈现；用户真正看到 PopupDownload / SafariAddHomeScreen 等 antd-mobile
// 组件时它们自身 lazy chunk 也会触发再次评估，因此把全局样式与组件 chunk
// 同步加载即可，不需要在 entry 评估期就阻塞。
// 注意：这里写在 __idleBoot 中，且不删除——只要这条 import 还在某处被调用，
// Vite 仍会保留 vendor-antd-mobile chunk；只是不再让它出现在 landing.html
// 顶部的 modulepreload 列表里，从关键路径上移除 ~280KB / 196KB gzip 下载。

function initCountryAndLanguage() {
  try {
    const countryList = window._config?.countryList || [];
    const languageList = window._config?.languageList || [];

    const findCountry = (countryId: string) =>
      countryList.find((c: any) => c.id === countryId);
    const findLanguage = (langId: string) =>
      languageList.find((l: any) => l.id === langId);

    let finalCountryId = "";
    const savedCountryId = getObject("countryId");
    if (savedCountryId && findCountry(savedCountryId)) {
      finalCountryId = savedCountryId;
    } else if (
      window._config?.countryId &&
      findCountry(window._config.countryId)
    ) {
      finalCountryId = window._config.countryId;
    } else if (countryList.length > 0) {
      finalCountryId = countryList[0].id;
    }

    if (finalCountryId) {
      const country = findCountry(finalCountryId);
      if (country) {
        setObject("countryId", country.id);
        setObject("currentCountry", country);
      }
    }

    let finalLanguageId = "";
    const savedLanguage = getObject("currentLanguage");
    if (savedLanguage && findLanguage(savedLanguage)) {
      finalLanguageId = savedLanguage;
    } else if (window._config?.lang && findLanguage(window._config.lang)) {
      finalLanguageId = window._config.lang;
    } else if (languageList.length > 0) {
      finalLanguageId = languageList[0].id;
    } else if (finalCountryId) {
      const country = findCountry(finalCountryId);
      if (country?.lang && findLanguage(country.lang)) {
        finalLanguageId = country.lang;
      }
    }

    if (finalLanguageId) {
      setObject("currentLanguage", finalLanguageId);
    }
  } catch (error) {
    console.error("初始化国家和语言失败:", error);
  }
}

// FCP/LCP 优化：preloadLandingStartup 改为动态 import utils 后再发起请求。
function preloadLandingStartup(): void {
  const ch =
    new URLSearchParams(window.location.search).get("ch") ||
    (window as any).__ch ||
    "";
  const startupPreload = ch
    ? import("./utils/utils")
        .then(({ default: utils }) =>
          utils.request("/api/home_launch/startup", { ch }, { alert: false })
        )
        .catch(() => null)
    : null;
  (window as any).__startupPreload = startupPreload;
}

const _root = createRoot(document.getElementById("root") as HTMLElement);

initCountryAndLanguage();
preloadLandingStartup();

// FCP/LCP 优化：
// - initPwa 必须保持同步立即执行：监听器需要在浏览器派发 beforeinstallprompt
//   之前注册，否则事件会溜走，导致 window.deferredPrompt 始终为 undefined，
//   PC Chrome 上点击安装会跳到首页（落地页是 PWA 安装的主要入口！）。
// - initWebPush 注册 Service Worker（/sw.js）：SW 只对"下一次"导航生效，
//   本次页面拿不到 SW 缓存收益；延迟 1-3s 注册不影响本次页面体验，但能把
//   SW 注册和 message 通道建立的开销让出 LCP 关键路径。
// - loopCheckPwa 实际只做 isApp + updateCheckIsPwa 两个本地检查，没有外部
//   依赖，延迟到 idle 不影响业务。
initPwa().catch((e: unknown) => console.error("initPwa failed:", e));

const __idleBoot = () => {
  // 把 antd-mobile 全局样式从 entry 评估期推到 idle 之后再加载，
  // 让 vendor-antd-mobile 不再进入 landing.html 的 modulepreload 列表
  import("antd-mobile/es/global").catch((e: unknown) =>
    console.error("antd-mobile/es/global load failed:", e)
  );
  initWebPush().catch((e: unknown) => console.error("initWebPush failed:", e));
  try {
    loopCheckPwa();
  } catch (e) {
    console.error("loopCheckPwa failed:", e);
  }
};
const _idleWin = window as unknown as {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
};
if (typeof _idleWin.requestIdleCallback === "function") {
  _idleWin.requestIdleCallback(__idleBoot, { timeout: 3000 });
} else {
  // Safari 没有原生 requestIdleCallback，setTimeout 兜底（1.5s 足够 LCP 完成）
  setTimeout(__idleBoot, 1500);
}

Promise.all([import("./pages/LandingPage"), import("react-router-dom")]).then(
  ([{ default: LandingPageWrapper }, { BrowserRouter }]) => {
    _root.render(
      <BrowserRouter future={{ v7_startTransition: true }}>
        <LandingPageWrapper />
      </BrowserRouter>
    );
    document.getElementById("root")?.classList.add("loaded");
  }
);
